import { db, auth } from "../firebaseConfig";
import { normalizeEmail } from "./utils";
import { getListingDisplayTitle } from "./listingTitle";
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  serverTimestamp,
  setDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  onSnapshot,
} from "firebase/firestore";

/**
 * Generates a 12-character URL-safe random string.
 */
export function generateShareId() {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
  let result = "";
  const array = new Uint8Array(12);
  crypto.getRandomValues(array);
  for (let i = 0; i < 12; i++) {
    result += chars[array[i] & 63];
  }
  return result;
}

// --------------- System administration ---------------

export function subscribeToMaintenanceStatus(onChange, onError) {
  return onSnapshot(doc(db, "system", "maintenance"), (snapshot) => {
    onChange({
      enabled: snapshot.exists(),
      message: snapshot.exists() ? snapshot.data().message || "" : "",
    });
  }, onError);
}

export async function enableMaintenanceMode(message, adminUid) {
  await setDoc(doc(db, "system", "maintenance"), {
    message: message.trim(),
    enabledBy: adminUid,
    enabledAt: serverTimestamp(),
  });
}

export async function disableMaintenanceMode() {
  await deleteDoc(doc(db, "system", "maintenance"));
}

function convertTimestamps(data) {
  return {
    ...data,
    createdAt: data.createdAt ? data.createdAt.toDate() : null,
    updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
  };
}

// --------------- Contact messages ---------------

export async function submitContactMessage({ name, email, subject, message }) {
  const docRef = await addDoc(collection(db, "contactMessages"), {
    name: name.trim(),
    email: normalizeEmail(email),
    subject: subject.trim(),
    message: message.trim(),
    status: "new",
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function getContactMessages() {
  const q = query(collection(db, "contactMessages"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((messageDoc) => ({
    id: messageDoc.id,
    ...convertTimestamps(messageDoc.data()),
  }));
}

export async function updateContactMessageStatus(messageId, status) {
  await updateDoc(doc(db, "contactMessages", messageId), {
    status,
    updatedAt: serverTimestamp(),
  });
}

// --------------- Portal CRUD ---------------

/**
 * Ensures a "Default" portal exists for the given user.
 * Returns the existing or newly-created default portal.
 */
export async function ensureDefaultPortal(uid) {
  const portalsRef = collection(db, "portals");
  const q = query(
    portalsRef,
    where("ownerUid", "==", uid),
    where("name", "==", "Default")
  );
  let snap;
  try {
    snap = await getDocs(q);
  } catch (e) {
    console.error("[ensureDefaultPortal] query failed", { uid }, e);
    throw e;
  }
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...convertTimestamps(d.data()) };
  }
  // Create one
  try {
    return await createPortal({ name: "Default", ownerUid: uid });
  } catch (e) {
    console.error("[ensureDefaultPortal] create failed", { uid }, e);
    throw e;
  }
}

export async function createPortal({ name, ownerUid, propertyId = null }) {
  const portalsRef = collection(db, "portals");
  const resolvedOwnerUid = ownerUid || (auth.currentUser ? auth.currentUser.uid : null);

  let shareId;
  let collision = true;
  while (collision) {
    shareId = generateShareId();
    const q = query(portalsRef, where("shareId", "==", shareId));
    const snap = await getDocs(q);
    collision = !snap.empty;
  }

  const docRef = await addDoc(portalsRef, {
    name,
    shareId,
    ownerUid: resolvedOwnerUid,
    owners: resolvedOwnerUid ? [resolvedOwnerUid] : [],
    propertyId: propertyId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    autoResponseEnabled: false,
    autoResponseTemplate: "",
  });

  return { id: docRef.id, name, shareId, ownerUid: resolvedOwnerUid, propertyId };
}

export async function getPortals() {
  const q = query(collection(db, "portals"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...convertTimestamps(d.data()),
  }));
}

export async function getPortalsByOwner(uid, email) {
  // Query portals where user is the original creator
  const ownerQuery = query(
    collection(db, "portals"),
    where("ownerUid", "==", uid)
  );
  // Query portals where user is in the owners array (UID-based managers)
  const ownersArrayQuery = query(
    collection(db, "portals"),
    where("owners", "array-contains", uid)
  );

  const queries = [getDocs(ownerQuery), getDocs(ownersArrayQuery)];

  // Also query portals where the user was added as a member by email
  if (email) {
    const memberEmailQuery = query(
      collection(db, "portals"),
      where("memberEmails", "array-contains", normalizeEmail(email))
    );
    queries.push(getDocs(memberEmailQuery));
  }

  const snaps = await Promise.all(queries);

  const portalMap = new Map();
  const addPortals = (snap) => {
    snap.docs.forEach((d) => {
      if (!portalMap.has(d.id)) {
        portalMap.set(d.id, { id: d.id, ...convertTimestamps(d.data()) });
      }
    });
  };
  snaps.forEach(addPortals);

  return Array.from(portalMap.values()).sort((a, b) => {
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return b.createdAt - a.createdAt;
  });
}

export async function getPortalById(portalId) {
  const ref = doc(db, "portals", portalId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) };
}

export async function getPortalByShareId(shareId) {
  const q = query(
    collection(db, "portals"),
    where("shareId", "==", shareId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...convertTimestamps(d.data()) };
}

export async function updatePortal(portalId, data) {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
}

/**
 * Bind a portal to a property (1:1). Clears the old binding on the previous
 * property's portal if needed — enforced client-side; Firestore just stores
 * the propertyId field on the portal doc.
 */
export async function bindPortalToProperty(portalId, propertyId) {
  await updateDoc(doc(db, "portals", portalId), {
    propertyId: propertyId || null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Find the portal bound to a specific property (returns null if none).
 */
export async function getPortalByPropertyId(propertyId) {
  const q = query(
    collection(db, "portals"),
    where("propertyId", "==", propertyId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...convertTimestamps(d.data()) };
}

export async function deletePortal(portalId) {
  const ticketsSnap = await getDocs(
    collection(db, "portals", portalId, "tickets")
  );

  if (!ticketsSnap.empty) {
    const batch = writeBatch(db);
    ticketsSnap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  await deleteDoc(doc(db, "portals", portalId));
}

// --------------- Portal Allowed Users ---------------

export async function addPortalAllowedEmail(portalId, email) {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, {
    allowedEmails: arrayUnion(normalizeEmail(email)),
    updatedAt: serverTimestamp(),
  });
}

export async function removePortalAllowedEmail(portalId, email) {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, {
    allowedEmails: arrayRemove(normalizeEmail(email)),
    updatedAt: serverTimestamp(),
  });
}

// --------------- Ticket CRUD ---------------

export async function createTicket(portalId, ticketData) {
  const ticketsRef = collection(db, "portals", portalId, "tickets");
  const currentUser = auth.currentUser;
  const docRef = await addDoc(ticketsRef, {
    ...ticketData,
    requesterUid: ticketData.requesterUid || (currentUser ? currentUser.uid : null),
    status: ticketData.status || "Open",
    adminNote: ticketData.adminNote || "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: docRef.id, portalId };
}

export async function getTicketsByPortal(portalId) {
  const q = query(
    collection(db, "portals", portalId, "tickets"),
    orderBy("createdAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    portalId,
    ...convertTimestamps(d.data()),
  }));
}

export async function getAllTickets() {
  const portalsSnap = await getDocs(collection(db, "portals"));
  const ticketSnaps = await Promise.all(
    portalsSnap.docs.map((portalDoc) =>
      getDocs(collection(db, "portals", portalDoc.id, "tickets")).then(
        (snap) => ({ portalDoc, snap })
      )
    )
  );

  const allTickets = [];
  for (const { portalDoc, snap } of ticketSnaps) {
    const portalData = portalDoc.data();
    snap.docs.forEach((d) => {
      allTickets.push({
        id: d.id,
        portalId: portalDoc.id,
        portalName: portalData.name,
        ...convertTimestamps(d.data()),
      });
    });
  }

  allTickets.sort((a, b) => {
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return b.createdAt - a.createdAt;
  });

  return allTickets;
}

export async function getTicketsByOwner(uid) {
  const ownedPortals = await getPortalsByOwner(uid);

  const ticketSnaps = await Promise.all(
    ownedPortals.map((portal) =>
      getDocs(
        query(
          collection(db, "portals", portal.id, "tickets"),
          orderBy("createdAt", "desc")
        )
      ).then((snap) => ({ portal, snap }))
    )
  );

  const tickets = [];
  for (const { portal, snap } of ticketSnaps) {
    snap.docs.forEach((d) => {
      tickets.push({
        id: d.id,
        portalId: portal.id,
        portalName: portal.name,
        ...convertTimestamps(d.data()),
      });
    });
  }

  tickets.sort((a, b) => {
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return b.createdAt - a.createdAt;
  });

  return tickets;
}

export async function getTicketById(portalId, ticketId) {
  const ref = doc(db, "portals", portalId, "tickets", ticketId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, portalId, ...convertTimestamps(snap.data()) };
}

export async function updateTicket(portalId, ticketId, data) {
  const ref = doc(db, "portals", portalId, "tickets", ticketId);
  await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
}

// --------------- Property Tickets (self-contained, no portal) ---------------
// Redesigned per user feedback: tenants should be able to file a ticket the
// moment they're added to a property — no lease/portal setup required. Each
// ticket lives at top-level `tickets/{ticketId}` with its own `messages`
// subcollection, and its own doc id doubles as a public shareable link
// (`/ticket/{ticketId}`) — see firestore.rules `allow get: if true`.

export async function createPropertyTicket({
  propertyId, propertyAddress, tenantId, tenantName,
  createdByUid, createdByEmail, title, description, severity,
}) {
  if (!propertyId) {
    throw new Error("Your account isn't linked to a property yet — ask your property owner to add you to one first.");
  }
  const property = await getPropertyById(propertyId);
  if (!property) {
    throw new Error("That property no longer exists.");
  }
  const pmUid = property.createdBy || "";
  const ref = await addDoc(collection(db, "tickets"), {
    propertyId,
    propertyAddress: propertyAddress || "",
    pmUid,
    tenantId: tenantId || "",
    tenantName: tenantName || "",
    createdByUid,
    createdByEmail: normalizeEmail(createdByEmail),
    title,
    description,
    severity: severity || "Low",
    status: "Open",
    adminNote: "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: ref.id, pmUid };
}


/** PM: every ticket across their whole portfolio — single equality query. */
export async function getTicketsByPmUid(uid) {
  if (!uid) return [];
  const q = query(collection(db, "tickets"), where("pmUid", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

/** Tenant/requester: only the tickets they filed. */
export async function getMyPropertyTickets(uid) {
  if (!uid) return [];
  const q = query(collection(db, "tickets"), where("createdByUid", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

/** All tickets filed by one tenant record (PM's tenant detail view). */
export async function getTicketsByTenantRecordId(tenantId) {
  if (!tenantId) return [];
  const q = query(collection(db, "tickets"), where("tenantId", "==", tenantId));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

/** Single ticket by id — also works unauthenticated via the public share link. */
export async function getPropertyTicketById(ticketId) {
  const snap = await getDoc(doc(db, "tickets", ticketId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) };
}

export async function updatePropertyTicket(ticketId, data) {
  await updateDoc(doc(db, "tickets", ticketId), { ...data, updatedAt: serverTimestamp() });
}

/** senderUid/senderEmail are omitted for anonymous replies via the public link. */
export async function addPropertyTicketMessage(ticketId, { senderUid, senderName, senderEmail, text }) {
  const ref = await addDoc(collection(db, "tickets", ticketId, "messages"), {
    senderUid: senderUid || null,
    senderName: senderName || "",
    senderEmail: senderEmail ? normalizeEmail(senderEmail) : "",
    text,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/** Subscribes to a property ticket's chat. Returns an unsubscribe function. */
export function onPropertyTicketMessages(ticketId, callback) {
  const q = query(collection(db, "tickets", ticketId, "messages"), orderBy("createdAt", "asc"));
  return onSnapshot(q, (snap) => {
    callback(
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
      }))
    );
  });
}

/**
 * Notifies the property owner + every tenant on the property (minus the
 * actor) about a ticket event — same recipient set as
 * notifyPropertyParticipants (property chat), just with ticket-specific
 * copy/links.
 */
export async function notifyPropertyTicketParticipants(propertyId, { type, title, message, excludeUid, ticketId }) {
  const property = await getPropertyById(propertyId);
  if (!property) return;

  // Only the property owner can list every tenant on a property (Firestore
  // rules restrict this for privacy) — a tenant-triggered event still needs
  // to notify the owner even though it can't enumerate co-tenants itself.
  let tenants = [];
  try {
    tenants = await getTenantsByProperty(propertyId);
  } catch (e) {
    console.warn("notifyPropertyTicketParticipants: couldn't list tenants (expected for non-owner callers):", e);
  }

  const recipients = new Map(); // uid -> tenant record id, or null for the owner
  if (property.createdBy) recipients.set(property.createdBy, null);
  tenants.forEach((t) => { if (t.uid) recipients.set(t.uid, t.id); });
  recipients.delete(excludeUid);

  const results = await Promise.allSettled(
    Array.from(recipients.entries()).map(([uid, tenantRecordId]) =>
      createNotification({
        userId: uid,
        type,
        title,
        message,
        // Tenants view tickets under /tenant/tickets, PMs under /tickets.
        link: tenantRecordId ? `/tenant/tickets/${ticketId}` : `/tickets/${ticketId}`,
      })
    )
  );
  // Log each failed recipient individually instead of letting one denial
  // fail the whole batch silently — pinpoints exactly which write is denied.
  results.forEach((r, i) => {
    if (r.status === "rejected") {
      const uid = Array.from(recipients.keys())[i];
      console.error(`notifyPropertyTicketParticipants: failed to notify uid=${uid}`, r.reason);
    }
  });
}


// --------------- My Tickets (role-aware) ---------------

export async function getMyTickets(uid, email) {
  // Only fetch portals the user owns or is a member of — avoids reading ALL portals.
  const ownedPortals = await getPortalsByOwner(uid, email);

  const ticketSnaps = await Promise.all(
    ownedPortals.map((portal) =>
      getDocs(
        query(
          collection(db, "portals", portal.id, "tickets"),
          orderBy("createdAt", "desc")
        )
      ).then((snap) => ({ portal, snap }))
    )
  );

  const tickets = [];
  for (const { portal, snap } of ticketSnaps) {
    snap.docs.forEach((d) => {
      tickets.push({
        id: d.id,
        portalId: portal.id,
        portalName: portal.name,
        ...convertTimestamps(d.data()),
      });
    });
  }

  tickets.sort((a, b) => {
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return b.createdAt - a.createdAt;
  });

  return tickets;
}

// --------------- Portal Owners ---------------

export async function addPortalOwner(portalId, ownerUid, role = "Member") {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, {
    owners: arrayUnion(ownerUid),
    [`ownerRoles.${ownerUid}`]: role,
    updatedAt: serverTimestamp(),
  });
}

export async function removePortalOwner(portalId, ownerUid) {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, {
    owners: arrayRemove(ownerUid),
    [`ownerRoles.${ownerUid}`]: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

export async function addPortalMemberEmail(portalId, email) {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, {
    memberEmails: arrayUnion(normalizeEmail(email)),
    updatedAt: serverTimestamp(),
  });
}

export async function removePortalMemberEmail(portalId, email) {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, {
    memberEmails: arrayRemove(normalizeEmail(email)),
    updatedAt: serverTimestamp(),
  });
}

// --------------- Notifications ---------------

/**
 * Creates a notification document in the top-level `notifications` collection.
 * `link` is the in-app route to navigate to on click — pass it explicitly for
 * any new notification type. Falls back to the old ticket-only route for
 * backward compatibility with callers that only pass portalId+ticketId.
 */
export async function createNotification({ userId, portalId, ticketId, type, title, message, link, listingId, propertyId }) {
  const notificationsRef = collection(db, "notifications");
  const payload = {
    userId,
    portalId: portalId || null,
    ticketId: ticketId || null,
    type,
    title: title || null,
    message,
    link: link || (portalId && ticketId ? `/tickets/${portalId}/${ticketId}` : null),
    createdAt: serverTimestamp(),
    readAt: null,
  };
  if (listingId) payload.listingId = listingId;
  if (propertyId) payload.propertyId = propertyId;
  await addDoc(notificationsRef, payload);
}

/**
 * Creates notifications for all owners of a portal (excluding the actor who triggered it).
 */
export async function notifyPortalOwners({ portalId, ticketId, type, title, message, link, excludeUid }) {
  const portal = await getPortalById(portalId);
  if (!portal) return;

  const owners = portal.owners ? [...portal.owners] : [];
  // Also include the legacy ownerUid if present and not already in owners
  if (portal.ownerUid && !owners.includes(portal.ownerUid)) {
    owners.push(portal.ownerUid);
  }

  const recipients = owners.filter((uid) => uid !== excludeUid);

  await Promise.all(
    recipients.map((uid) =>
      createNotification({ userId: uid, portalId, ticketId, type, title, message, link })
    )
  );
}

/**
 * One-time (non-realtime) fetch of a user's full notification history, most
 * recent first, for the paginated Notification Center page. The bell
 * dropdown keeps using the realtime `onUnreadNotifications` listener instead.
 * Sorts/caps client-side (single equality where-clause, like
 * onUnreadNotifications) rather than orderBy, avoiding a composite index.
 */
export async function getNotificationsByUser(userId, limitCount = 200) {
  if (!userId) return [];
  const q = query(collection(db, "notifications"), where("userId", "==", userId));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
    }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    .slice(0, limitCount);
}

/**
 * Subscribes to unread notifications for a given user in realtime.
 * Returns an unsubscribe function.
 */
export function onUnreadNotifications(userId, callback) {
  const q = query(
    collection(db, "notifications"),
    where("userId", "==", userId)
  );
  return onSnapshot(q, (snap) => {
    const notifications = snap.docs
      .map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
      }))
      .filter((n) => n.readAt == null)
      .sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt - a.createdAt;
      });
    callback(notifications);
  });
}

/**
 * Marks a single notification as read by setting readAt to a server timestamp.
 */
export async function markNotificationRead(notificationId) {
  const ref = doc(db, "notifications", notificationId);
  await updateDoc(ref, { readAt: serverTimestamp() });
}

/**
 * Marks all unread notifications for a user as read.
 */
export async function markAllNotificationsRead(userId) {
  const q = query(
    collection(db, "notifications"),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  const unread = snap.docs.filter((d) => d.data().readAt == null);
  if (unread.length === 0) return;

  const batch = writeBatch(db);
  unread.forEach((d) => {
    batch.update(d.ref, { readAt: serverTimestamp() });
  });
  await batch.commit();
}

/**
 * Marks all notifications for a specific ticket+user as read.
 * Used when a user navigates to a ticket from anywhere in the UI.
 */
export async function markTicketNotificationsRead(userId, ticketId) {
  const q = query(
    collection(db, "notifications"),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  const unread = snap.docs.filter(
    (d) => d.data().ticketId === ticketId && d.data().readAt == null
  );
  if (unread.length === 0) return;

  const batch = writeBatch(db);
  unread.forEach((d) => {
    batch.update(d.ref, { readAt: serverTimestamp() });
  });
  await batch.commit();
}

// --------------- Tenants ---------------

/**
 * Checks if a tenant with this email already exists in any portal.
 * Returns the tenant doc (with portalId) if found, or null.
 * Only returns active (non-deleted) tenants.
 */
export async function getTenantByEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;
  const q = query(
    collection(db, "tenants"),
    where("email", "==", normalized)
  );
  const snap = await getDocs(q);
  // Prefer active records first; fall back to any match
  const active = snap.docs.find((d) => !d.data().deleted);
  const d = active || snap.docs[0];
  if (!d) return null;
  return { id: d.id, ...convertTimestamps(d.data()) };
}

/**
 * Like getTenantByEmail but explicitly returns deleted tenants too.
 * Used for the duplicate-email create guard.
 */
export async function getTenantByEmailAny(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;
  const q = query(collection(db, "tenants"), where("email", "==", normalized));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...convertTimestamps(d.data()) };
}

/**
 * Returns ALL tenant records for a given email (one per property they rent).
 * Used in the tenant portal to build the property switcher.
 */
export async function getTenantsByEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return [];
  const q = query(collection(db, "tenants"), where("email", "==", normalized));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .filter((d) => !d.deleted);
}

export async function createTenant(tenantData) {
  const tenantsRef = collection(db, "tenants");
  const email = normalizeEmail(tenantData.email);
  const docRef = await addDoc(tenantsRef, {
    name: "",
    inviteStatus: "pending",
    ...tenantData,
    email,
    uid: tenantData.uid || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: docRef.id, ...tenantData, email };
}

/**
 * Add a tenant to a property using only their email address — no tenant record
 * needs to be created up front by the user. Creates (or re-activates) the
 * tenant doc and sends the invitation the person accepts on sign-in.
 *
 * `existingTenants` should be the property's current tenant list so a
 * previously-declined person can be re-invited without creating a duplicate.
 */
export async function addTenantByEmail(
  propertyId,
  email,
  { createdByUid, leaseId = null, existingTenants = [] } = {}
) {
  const _email = normalizeEmail(email);
  if (!_email) throw new Error("An email address is required.");

  const existing = existingTenants.find(
    (t) => normalizeEmail(t.email) === _email && !t.deleted
  );
  if (existing && existing.inviteStatus !== "declined") {
    throw new Error("That email has already been added to this property.");
  }

  let targetId;
  if (existing) {
    // Previously declined — reset the record instead of creating a duplicate.
    targetId = existing.id;
    await updateTenant(existing.id, {
      propertyId,
      leaseId,
      uid: null,
      inviteStatus: "pending",
      declinedAt: null,
      deleted: false,
    });
  } else {
    const created = await createTenant({
      name: "",
      email: _email,
      propertyId,
      leaseId,
      inviteStatus: "pending",
      createdBy: createdByUid || null,
    });
    targetId = created.id;
  }

  await createInvitation({
    email: _email,
    role: "tenant",
    targetId,
    leaseId,
    propertyId,
    createdByUid,
  });

  // Grants the tenant access to the property's group chat.
  await updateDoc(doc(db, "properties", propertyId), {
    tenantEmails: arrayUnion(_email),
    updatedAt: serverTimestamp(),
  });

  return targetId;
}

/**
 * Attaches an already-existing property tenant to a payment setup (lease)
 * that was created without them (or before they existed) — used from the
 * Tenants pane so tenant assignment for payments never requires re-picking
 * them during lease setup. No invitation is sent here; the tenant was
 * already invited once, when addTenantByEmail first added them to the
 * property. Firestore's `leases` rule (and the createRentPaymentIntent
 * Lambda) both check these lease-level arrays, not the tenant doc's
 * `leaseId` field, so both must be updated for the tenant to actually be
 * able to pay.
 */
export async function addTenantToLease(leaseId, { tenantId, email, name }) {
  await Promise.all([
    updateDoc(doc(db, "leases", leaseId), {
      tenantIds: arrayUnion(tenantId),
      tenantEmails: arrayUnion(normalizeEmail(email)),
      tenantNames: arrayUnion(name || email),
      updatedAt: serverTimestamp(),
    }),
    updateTenant(tenantId, { leaseId }),
  ]);
}

/** Revokes a former tenant's access to the property group chat. */
export async function removePropertyTenantEmail(propertyId, email) {
  const _email = normalizeEmail(email);
  if (!propertyId || !_email) return;
  await updateDoc(doc(db, "properties", propertyId), {
    tenantEmails: arrayRemove(_email),
    updatedAt: serverTimestamp(),
  });
}

export async function getTenantsByProperty(propertyId) {
  const q = query(
    collection(db, "tenants"),
    where("propertyId", "==", propertyId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .filter((d) => !d.deleted)
    .sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt - a.createdAt;
    });
}

export async function getTenantsByPortal(portalId) {
  const propSnap = await getDocs(
    query(collection(db, "properties"), where("portalId", "==", portalId))
  );
  const propertyIds = propSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d) => !d.deleted)
    .map((d) => d.id);
  if (propertyIds.length === 0) return [];

  const BATCH = 30;
  const allDocs = [];
  for (let i = 0; i < propertyIds.length; i += BATCH) {
    const batch = propertyIds.slice(i, i + BATCH);
    const snap = await getDocs(
      query(collection(db, "tenants"), where("propertyId", "in", batch))
    );
    snap.docs.forEach((d) => allDocs.push({ id: d.id, ...convertTimestamps(d.data()) }));
  }
  return allDocs
    .filter((d) => !d.deleted)
    .sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt - a.createdAt;
    });
}

export async function getDeletedTenantsByPortal(portalId) {
  const propSnap = await getDocs(
    query(collection(db, "properties"), where("portalId", "==", portalId))
  );
  const propertyIds = propSnap.docs.map((d) => d.id);
  if (propertyIds.length === 0) return [];

  const BATCH = 30;
  const allDocs = [];
  for (let i = 0; i < propertyIds.length; i += BATCH) {
    const batch = propertyIds.slice(i, i + BATCH);
    const snap = await getDocs(
      query(collection(db, "tenants"), where("propertyId", "in", batch))
    );
    snap.docs.forEach((d) => allDocs.push({ id: d.id, ...convertTimestamps(d.data()) }));
  }
  return allDocs
    .filter((d) => d.deleted === true)
    .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));
}

export async function getTenantById(tenantId) {
  const ref = doc(db, "tenants", tenantId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) };
}

export async function updateTenant(tenantId, data) {
  const ref = doc(db, "tenants", tenantId);
  await updateDoc(ref, {
    ...data,
    ...(data.email ? { email: normalizeEmail(data.email) } : {}),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTenant(tenantId) {
  // Soft-delete: flag the record and clear the property assignment so portal access is revoked.
  await updateDoc(doc(db, "tenants", tenantId), {
    deleted: true,
    deletedAt: serverTimestamp(),
    propertyId: "",
    updatedAt: serverTimestamp(),
  });
}

export async function restoreTenant(tenantId) {
  await updateDoc(doc(db, "tenants", tenantId), {
    deleted: false,
    deletedAt: null,
    updatedAt: serverTimestamp(),
  });
}

// --------------- Properties ---------------

// --------------- Activity Log (audit trail) ---------------

/**
 * Records one immutable entry in a PM/landlord's activity log. `uid` is
 * whose log this belongs to (the portfolio owner) — not necessarily the
 * actor who triggered it, since some future events (a tenant paying rent,
 * an applicant applying) will need to log to the *landlord's* log.
 */
export async function logActivity({ uid, type, title, message, link }) {
  if (!uid) return;
  await addDoc(collection(db, "activityLog"), {
    uid,
    type,
    title: title || null,
    message,
    link: link || null,
    createdAt: serverTimestamp(),
  });
}

/**
 * One-time fetch of a PM's full activity history, most recent first.
 * Sorts/caps client-side (single equality where-clause) to avoid a
 * composite index, same as getNotificationsByUser.
 */
export async function getActivityLog(uid, limitCount = 300) {
  if (!uid) return [];
  const q = query(collection(db, "activityLog"), where("uid", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
    }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    .slice(0, limitCount);
}

export async function createProperty({ address, createdBy }) {
  const propertiesRef = collection(db, "properties");
  const docRef = await addDoc(propertiesRef, {
    address: (address || "").trim(),
    createdBy,
    status: "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  logActivity({
    uid: createdBy,
    type: "property_added",
    title: "Property added",
    message: `Added ${(address || "").trim()}`,
    link: `/properties/${docRef.id}`,
  }).catch((e) => console.error("Log activity error:", e));
  return { id: docRef.id, address, createdBy, status: "pending" };
}

export async function getPropertiesByOwner(uid) {
  const q = query(
    collection(db, "properties"),
    where("createdBy", "==", uid)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .filter((d) => !d.deleted)
    .sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt - a.createdAt;
    });
}

export async function getDeletedPropertiesByOwner(uid) {
  const q = query(collection(db, "properties"), where("createdBy", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .filter((d) => d.deleted === true)
    .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));
}

export async function getPropertyById(propertyId) {
  const ref = doc(db, "properties", propertyId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) };
}

export async function updateProperty(propertyId, data) {
  const ref = doc(db, "properties", propertyId);
  await updateDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteProperty(propertyId) {
  await updateDoc(doc(db, "properties", propertyId), {
    deleted: true,
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function restoreProperty(propertyId) {
  await updateDoc(doc(db, "properties", propertyId), {
    deleted: false,
    deletedAt: null,
    updatedAt: serverTimestamp(),
  });
}

// --------------- Property Access Sharing ---------------
// A property owner (createdBy) can share a property with other people by email.
//   sharedEmails[]  — every email with access (viewers AND editors)
//   sharedEditors[] — the subset that may also make changes
// Two arrays rather than a map so Firestore rules and array-contains queries stay simple.

export const PROPERTY_ACCESS_VIEWER = "viewer";
export const PROPERTY_ACCESS_EDITOR = "editor";

/** Grant a person access to a property. Re-granting updates their role. */
export async function sharePropertyAccess(propertyId, email, role = PROPERTY_ACCESS_VIEWER) {
  const _email = normalizeEmail(email);
  if (!_email) throw new Error("An email address is required.");
  await updateDoc(doc(db, "properties", propertyId), {
    sharedEmails: arrayUnion(_email),
    sharedEditors:
      role === PROPERTY_ACCESS_EDITOR ? arrayUnion(_email) : arrayRemove(_email),
    updatedAt: serverTimestamp(),
  });
}

/** Change an existing collaborator's role without re-adding them. */
export async function setPropertyAccessRole(propertyId, email, role) {
  return sharePropertyAccess(propertyId, email, role);
}

/** Remove a person's access entirely. */
export async function revokePropertyAccess(propertyId, email) {
  const _email = normalizeEmail(email);
  await updateDoc(doc(db, "properties", propertyId), {
    sharedEmails: arrayRemove(_email),
    sharedEditors: arrayRemove(_email),
    updatedAt: serverTimestamp(),
  });
}

/**
 * The access list for a property, shaped for the UI.
 * Returns [{ email, role }] sorted alphabetically.
 */
export function getPropertyAccessList(property) {
  const editors = new Set(property?.sharedEditors || []);
  return (property?.sharedEmails || [])
    .map((email) => ({
      email,
      role: editors.has(email) ? PROPERTY_ACCESS_EDITOR : PROPERTY_ACCESS_VIEWER,
    }))
    .sort((a, b) => a.email.localeCompare(b.email));
}

/** Properties shared with this email by someone else (excludes ones they own). */
export async function getPropertiesSharedWithEmail(email) {
  const _email = normalizeEmail(email);
  if (!_email) return [];
  const q = query(
    collection(db, "properties"),
    where("sharedEmails", "array-contains", _email)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .filter((d) => !d.deleted)
    .sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt - a.createdAt;
    });
}

/** True when the user owns the property or has been given editor access. */
export function canEditProperty(property, user) {
  if (!property || !user) return false;
  if (property.createdBy === user.uid) return true;
  const _email = normalizeEmail(user.email);
  return !!_email && (property.sharedEditors || []).includes(_email);
}

// --------------- Admin ---------------

export async function isUserAdmin(uid) {
  const snap = await getDoc(doc(db, "admins", uid));
  return snap.exists();
}

// --------------- Users ---------------

export async function createUserRecord(uid, email, displayName = "") {
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      uid,
      email: email || "",
      displayName: displayName || "",
      accountStatus: "active",
      createdAt: serverTimestamp(),
    });
  }
  return (await getDoc(ref)).data();
}

export async function getUserRecord(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { id: snap.id, ...convertTimestamps(snap.data()) } : null;
}

export async function getAllUsers() {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function deleteUserRecord(uid) {
  await deleteDoc(doc(db, "users", uid));
}

/**
 * Pauses a user's account: deactivates every rental listing they created
 * (so their properties stop accepting new applicants) and notifies them.
 * That's the whole effect — it doesn't otherwise restrict their access.
 */
export async function pauseUserAccount(uid, adminUid) {
  await updateDoc(doc(db, "users", uid), {
    accountStatus: "paused",
    pausedBy: adminUid,
    pausedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const snap = await getDocs(query(collection(db, "listings"), where("createdBy", "==", uid)));
  await Promise.all(
    snap.docs
      .filter((d) => d.data().active !== false)
      .map((d) => updateDoc(d.ref, { active: false, updatedAt: serverTimestamp() }))
  );

  await createNotification({
    userId: uid,
    type: "account_paused",
    title: "Your account has been paused",
    message: "An administrator has paused your account and deactivated your active rental listings.",
    link: "/profile",
  });
}

export async function unpauseUserAccount(uid, adminUid) {
  await updateDoc(doc(db, "users", uid), {
    accountStatus: "active",
    unpausedBy: adminUid,
    unpausedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await createNotification({
    userId: uid,
    type: "account_unpaused",
    title: "Your account has been reactivated",
    message: "An administrator has reactivated your account.",
    link: "/profile",
  });
}

// --------------- Rental Listings ---------------

export function toPublicListing(listingId, data) {
  const bedrooms = data.bedrooms ?? "";
  const title = getListingDisplayTitle({
    title: data.publicTitle || data.title,
    address: data.address,
    bedrooms,
    city: data.city,
  });

  return {
    listingId,
    title,
    city: data.city || "",
    stateCode: data.stateCode || "",
    neighborhood: data.neighborhood || "",
    monthlyRent: data.monthlyRent || 0,
    bedrooms,
    bathrooms: data.bathrooms ?? "",
    sqft: data.sqft || "",
    availableDate: data.availableDate || "",
    petPolicy: data.petPolicy || "",
    parkingIncluded: data.parkingIncluded || false,
    thumbnailKey: data.images?.[0]?.key || "",
    createdAt: data.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

export async function createListing(data) {
  const ref = doc(collection(db, "listings"));
  const listingData = {
    propertyId: data.propertyId,
    createdBy: data.createdBy,
    title: data.title || "",
    description: data.description || "",
    monthlyRent: data.monthlyRent || 0,
    bedrooms: data.bedrooms || "",
    bathrooms: data.bathrooms || "",
    sqft: data.sqft || "",
    deposit: data.deposit || "",
    availableDate: data.availableDate || "",
    address: data.address || "",
    petPolicy: data.petPolicy || "",
    parkingIncluded: data.parkingIncluded || false,
    parkingType: data.parkingType || "",
    utilitiesIncluded: data.utilitiesIncluded || [],
    amenities: data.amenities || [],
    leaseTerm: data.leaseTerm || "",
    laundry: data.laundry || "",
    floor: data.floor || "",
    yearBuilt: data.yearBuilt || "",
    smokingPolicy: data.smokingPolicy || "",
    nearbyPlaces: data.nearbyPlaces || null,
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, listingData);
  await setDoc(doc(db, "publicListings", ref.id), toPublicListing(ref.id, listingData)).catch((error) => {
    console.warn("Public listing projection create failed:", error);
  });
  return ref.id;
}

export async function deleteListing(listingId) {
  await deleteDoc(doc(db, "publicListings", listingId)).catch((error) => {
    console.warn("Public listing projection delete failed:", error);
  });
  await deleteDoc(doc(db, "listings", listingId));
}

export async function deleteApplicationsByListing(listingId) {
  const q = query(
    collection(db, "rentalApplications"),
    where("listingId", "==", listingId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return 0;
  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
  return snap.size;
}

export async function getAllActiveListings() {
  const q = query(
    collection(db, "listings"),
    where("active", "==", true)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getAllPublicListings() {
  const snap = await getDocs(collection(db, "publicListings"));
  return snap.docs
    .map((listingDocSnapshot) => {
      const data = convertTimestamps(listingDocSnapshot.data());
      return {
        id: listingDocSnapshot.id,
        ...data,
        images: data.thumbnailKey ? [{ key: data.thumbnailKey }] : [],
      };
    })
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function syncPublicListings(listings) {
  if (!listings.length) return;
  const batch = writeBatch(db);
  listings.forEach((listing) => {
    const publicRef = doc(db, "publicListings", listing.id);
    if (listing.active === false) batch.delete(publicRef);
    else batch.set(publicRef, toPublicListing(listing.id, listing));
  });
  await batch.commit();
}

export async function getListingsByProperty(propertyId) {
  const q = query(
    collection(db, "listings"),
    where("propertyId", "==", propertyId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

/** PM: every listing they created, across every property \u2014 for the Listings inbox page. */
export async function getListingsByOwner(uid) {
  if (!uid) return [];
  const q = query(collection(db, "listings"), where("createdBy", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getListingById(listingId) {
  const snap = await getDoc(doc(db, "listings", listingId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) };
}

export async function updateListing(listingId, data) {
  const listingRef = doc(db, "listings", listingId);
  const snapshot = await getDoc(listingRef);
  if (!snapshot.exists()) throw new Error("Listing not found.");

  const nextListing = {
    ...snapshot.data(),
    ...data,
    updatedAt: serverTimestamp(),
  };
  await updateDoc(listingRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
  const publicRef = doc(db, "publicListings", listingId);
  const projectionWrite = nextListing.active === false
    ? deleteDoc(publicRef)
    : setDoc(publicRef, toPublicListing(listingId, nextListing));
  await projectionWrite.catch((error) => {
    console.warn("Public listing projection update failed:", error);
  });
}

export async function addListingImage(listingId, imageData) {
  await updateDoc(doc(db, "listings", listingId), {
    images: arrayUnion(imageData),
    updatedAt: serverTimestamp(),
  });
}

export async function removeListingImage(listingId, imageData) {
  await updateDoc(doc(db, "listings", listingId), {
    images: arrayRemove(imageData),
    updatedAt: serverTimestamp(),
  });
}

// --------------- Listing feedback ---------------

const FEEDBACK_TOPICS = new Set([
  "photos",
  "fees",
  "pet_policy",
  "tour_info",
  "application_process",
  "neighborhood",
]);

export async function submitListingFeedback({ listingId, rating, topics = [], message = "" }) {
  if (!listingId) throw new Error("Listing is required.");
  const numericRating = Number(rating);
  if (!Number.isInteger(numericRating) || numericRating < 1 || numericRating > 5) {
    throw new Error("Choose a rating before submitting feedback.");
  }

  const listing = await getListingById(listingId);
  if (!listing || listing.active === false) throw new Error("This listing is not accepting feedback.");

  const cleanTopics = [...new Set(topics)]
    .filter((topic) => FEEDBACK_TOPICS.has(topic))
    .slice(0, 6);
  const cleanMessage = message.trim().slice(0, 1000);

  const feedbackRef = await addDoc(collection(db, "listingFeedback"), {
    listingId,
    propertyId: listing.propertyId || "",
    pmUid: listing.createdBy || "",
    rating: numericRating,
    topics: cleanTopics,
    message: cleanMessage,
    createdAt: serverTimestamp(),
  });

  if (listing.createdBy && listing.propertyId) {
    createNotification({
      userId: listing.createdBy,
      type: "listing_feedback",
      title: "New listing feedback",
      message: `A renter rated your listing ${numericRating} out of 5.`,
      link: `/properties/${listing.propertyId}`,
      listingId,
      propertyId: listing.propertyId,
    }).catch((error) => {
      console.error("Notify listing feedback error:", error);
    });
  }

  return feedbackRef.id;
}

export async function getListingFeedbackByProperty(propertyId, pmUid) {
  if (!propertyId || !pmUid) return [];
  const snap = await getDocs(query(
    collection(db, "listingFeedback"),
    where("propertyId", "==", propertyId),
    where("pmUid", "==", pmUid)
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

// --------------- Rental Applications ---------------

const appToDate = (v) =>
  v && typeof v.toDate === "function" ? v.toDate() : v || null;

function convertApplication(d) {
  return {
    id: d.id,
    ...d.data(),
    submittedAt: appToDate(d.data().submittedAt),
    updatedAt: appToDate(d.data().updatedAt),
  };
}

// Draft data collected before the applicant pays the $50 fee. Never becomes a
// real (PM-visible) rentalApplications doc unless/until stripeWebhook.py
// promotes it after payment succeeds — see createApplicationFeePaymentIntent.
export async function createPendingApplication(targetId, data) {
  // Backward compatibility:
  // - Legacy calls pass (listingId, { propertyId, ... })
  // - New flow can pass (propertyId, { ... }) with no listingId
  const propertyId = data.propertyId || targetId || "";
  const listingId = data.listingId || (data.propertyId ? targetId : null);
  if (!propertyId) throw new Error("propertyId is required for rental applications");

  // DOB is visible to both the applicant and the property owner, same as
  // every other field here — only SSN is intentionally never accepted/stored
  // anywhere in Firestore (used solely, in-memory, to kick off Checkr).
  const firstName = (data.firstName || "").trim();
  const lastName = (data.lastName || "").trim();

  const ref = await addDoc(collection(db, "pendingApplications"), {
    listingId,
    propertyId,
    applicantUid: data.applicantUid || null,
    firstName,
    lastName,
    name: `${firstName} ${lastName}`.trim(),
    email: data.email || "",
    phone: data.phone || "",
    employer: data.employer || "",
    monthlyIncome: data.monthlyIncome || "",
    dob: data.dob || "",
    message: data.message || "",
    additionalApplicants: data.additionalApplicants || [],
    // Occupants who won't complete their own application (no separate form,
    // no separate fee) — just a headcount shown to the property owner.
    minorsCount: Number(data.minorsCount) || 0,
    applicationFeeAmount: data.applicationFeeAmount || 0,
    createdAt: serverTimestamp(),
  });

  return ref.id;
}

// A co-applicant may decline their own invite (rules restrict this to only
// setting status -> "declined", matched by their own uid/email).
export async function declineApplicantInvite(applicationId, applicantId) {
  await updateDoc(doc(db, "rentalApplications", applicationId, "applicants", applicantId), {
    status: "declined",
    declinedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// Same proof-of-income pattern as the primary applicant's own
// addIncomeDocument, scoped to one co-applicant's sub-doc (rules restrict
// this to the co-applicant themself, matched by uid/email).
export async function addApplicantIncomeDocument(applicationId, applicantId, docData) {
  await updateDoc(doc(db, "rentalApplications", applicationId, "applicants", applicantId), {
    incomeDocuments: arrayUnion(docData),
    updatedAt: serverTimestamp(),
  });
}

// The primary applicant (or the property's PM) may remove a co-applicant
// from their household entirely.
export async function removeApplicant(applicationId, applicantId) {
  await deleteDoc(doc(db, "rentalApplications", applicationId, "applicants", applicantId));
}

export async function getApplicationsByListing(listingId) {
  const q = query(
    collection(db, "rentalApplications"),
    where("listingId", "==", listingId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(convertApplication)
    .sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
}

export async function getApplicationsByProperty(propertyId) {
  const q = query(
    collection(db, "rentalApplications"),
    where("propertyId", "==", propertyId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(convertApplication)
    .sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
}

export async function getApplicationsByApplicant(uid) {
  const q = query(
    collection(db, "rentalApplications"),
    where("applicantUid", "==", uid)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(convertApplication)
    .sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
}

export async function updateApplicationStatus(applicationId, status) {
  await updateDoc(doc(db, "rentalApplications", applicationId), {
    status,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Appends one uploaded proof-of-income document (S3 key + metadata, no file
 * bytes) onto the application. Readable by both the applicant and the
 * property owner, same as every other field on this doc.
 */
export async function addIncomeDocument(applicationId, docData) {
  await updateDoc(doc(db, "rentalApplications", applicationId), {
    incomeDocuments: arrayUnion(docData),
    updatedAt: serverTimestamp(),
  });
}

// --------------- Public Landlord Profile ---------------

export async function getPublicLandlordProfile(uid) {
  const snap = await getDoc(doc(db, "publicProfiles", uid));
  return snap.exists() ? { id: snap.id, ...convertTimestamps(snap.data()) } : null;
}

// --------------- Tenant Documents (Firebase Storage) ---------------

export async function uploadTenantDocument(tenantId, file) {
  const { ref: storageRef, uploadBytes, getDownloadURL } = await import("firebase/storage");
  const { storage } = await import("../firebaseConfig");
  const fileRef = storageRef(storage, `tenants/${tenantId}/documents/${file.name}`);
  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);
  return { name: file.name, url };
}

export async function getTenantDocuments(tenantId) {
  const { ref: storageRef, listAll, getDownloadURL } = await import("firebase/storage");
  const { storage } = await import("../firebaseConfig");
  const folderRef = storageRef(storage, `tenants/${tenantId}/documents`);
  const result = await listAll(folderRef);
  const files = await Promise.all(
    result.items.map(async (itemRef) => {
      const url = await getDownloadURL(itemRef);
      return { name: itemRef.name, url };
    })
  );
  return files;
}

export async function deleteTenantDocument(tenantId, fileName) {
  const { ref: storageRef, deleteObject } = await import("firebase/storage");
  const { storage } = await import("../firebaseConfig");
  const fileRef = storageRef(storage, `tenants/${tenantId}/documents/${fileName}`);
  await deleteObject(fileRef);
}

// --------------- Support Groups ---------------

export async function createSupportGroup({ name, memberEmails, createdBy }) {
  const groupsRef = collection(db, "supportGroups");
  const docRef = await addDoc(groupsRef, {
    name,
    memberEmails: (memberEmails || []).map(normalizeEmail),
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: docRef.id, name, memberEmails };
}

export async function getSupportGroups(createdBy) {
  const q = query(
    collection(db, "supportGroups"),
    where("createdBy", "==", createdBy)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => {
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      return b.createdAt - a.createdAt;
    });
}

export async function getSupportGroupById(groupId) {
  const ref = doc(db, "supportGroups", groupId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) };
}

export async function updateSupportGroup(groupId, data) {
  const ref = doc(db, "supportGroups", groupId);
  await updateDoc(ref, {
    ...data,
    ...(data.memberEmails
      ? { memberEmails: data.memberEmails.map(normalizeEmail) }
      : {}),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteSupportGroup(groupId) {
  await deleteDoc(doc(db, "supportGroups", groupId));
}

export async function addGroupToPortal(portalId, groupId) {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, {
    assignedGroups: arrayUnion(groupId),
    updatedAt: serverTimestamp(),
  });
}

export async function removeGroupFromPortal(portalId, groupId) {
  const ref = doc(db, "portals", portalId);
  await updateDoc(ref, {
    assignedGroups: arrayRemove(groupId),
    updatedAt: serverTimestamp(),
  });
}

// --------------- Support Members (aggregated view) ---------------

/**
 * Aggregates all unique support member emails across portals owned by the user.
 * Returns an array of { email, portalIds, portalNames }.
 */
export async function getAllSupportMembers(uid) {
  const portals = await getPortalsByOwner(uid);
  const memberMap = new Map();

  for (const portal of portals) {
    const emails = portal.memberEmails || [];
    for (const email of emails) {
      if (!memberMap.has(email)) {
        memberMap.set(email, { email, portalIds: [], portalNames: [] });
      }
      const entry = memberMap.get(email);
      entry.portalIds.push(portal.id);
      entry.portalNames.push(portal.name);
    }
  }

  return Array.from(memberMap.values());
}

/**
 * Aggregates all tenants across all portals owned by the user.
 * Returns an array of tenant objects each enriched with portalId and portalName.
 */
export async function getAllTenants(uid) {
  // Single path: owner's properties → tenants on those properties.
  // Avoids the redundant portal→property→tenant chain.
  const allProperties = await getPropertiesByOwner(uid);
  const allPropertyIds = allProperties.map((p) => p.id);

  // Build a propertyId→portalId lookup so we can resolve portalNames
  const portalCache = new Map();
  const enrichWithPortal = async (tenant) => {
    if (!tenant.propertyId) return tenant;
    const prop = allProperties.find((p) => p.id === tenant.propertyId);
    if (!prop?.portalId) return tenant;
    if (!portalCache.has(prop.portalId)) {
      const portal = await getPortalById(prop.portalId);
      portalCache.set(prop.portalId, portal?.name || "");
    }
    return { ...tenant, portalName: portalCache.get(prop.portalId) };
  };

  if (allPropertyIds.length === 0) return [];

  const BATCH = 30;
  const seen = new Set();
  const allDocs = [];

  for (let i = 0; i < allPropertyIds.length; i += BATCH) {
    const batch = allPropertyIds.slice(i, i + BATCH);
    const snap = await getDocs(
      query(collection(db, "tenants"), where("propertyId", "in", batch))
    );
    for (const d of snap.docs) {
      if (seen.has(d.id)) continue;
      const t = { id: d.id, ...convertTimestamps(d.data()) };
      if (t.deleted) continue;
      seen.add(d.id);
      allDocs.push(await enrichWithPortal(t));
    }
  }

  return allDocs.sort((a, b) => {
    if (!a.createdAt) return 1;
    if (!b.createdAt) return -1;
    return b.createdAt - a.createdAt;
  });
}

// --------------- Property Group Chat ---------------
// One conversation per property, shared by the owner, everyone the property is
// shared with, and every tenant on it. Access is granted through the property's
// sharedEmails / tenantEmails arrays so the security rules stay a simple lookup.

export async function addPropertyMessage(propertyId, { senderUid, senderName, senderEmail, text }) {
  const ref = await addDoc(collection(db, "properties", propertyId, "messages"), {
    senderUid: senderUid || null,
    senderName: senderName || "",
    senderEmail: normalizeEmail(senderEmail),
    text,
    createdAt: serverTimestamp(),
  });
  notifyPropertyParticipants(propertyId, {
    type: "property_message",
    title: "New message",
    message: `${senderName || "Someone"}: ${text.length > 80 ? text.slice(0, 80) + "…" : text}`,
    excludeUid: senderUid,
  }).catch((e) => console.error("Notify property participants error:", e));
  return ref.id;
}

/**
 * Notifies everyone on a property's group chat except the sender \u2014 the
 * owner plus every tenant we already have a uid for (a tenant who hasn't
 * accepted their invitation yet has no uid to notify). Doesn't yet cover
 * shared editors/viewers (sharedEmails/sharedEditors), since those are
 * stored by email with no reverse email\u2192uid lookup available client-side.
 */
export async function notifyPropertyParticipants(propertyId, { type, title, message, excludeUid }) {
  const property = await getPropertyById(propertyId);
  if (!property) return;

  // Only the property owner can list every tenant on a property (Firestore
  // rules restrict this for privacy) — a tenant-triggered message still
  // needs to notify the owner even though it can't enumerate co-tenants itself.
  let tenants = [];
  try {
    tenants = await getTenantsByProperty(propertyId);
  } catch (e) {
    console.warn("notifyPropertyParticipants: couldn't list tenants (expected for non-owner callers):", e);
  }

  const recipients = new Map(); // uid -> tenant record id, or null for the owner
  if (property.createdBy) recipients.set(property.createdBy, null);
  tenants.forEach((t) => { if (t.uid) recipients.set(t.uid, t.id); });
  recipients.delete(excludeUid);

  const results = await Promise.allSettled(
    Array.from(recipients.entries()).map(([uid, tenantRecordId]) =>
      createNotification({
        userId: uid,
        type,
        title,
        message,
        // Tenants read the chat inside their rental page's Chat tab; PMs land
        // on the property page and open the Owners/Tenants tab themselves.
        link: tenantRecordId ? `/tenant/my-rentals/${tenantRecordId}?tab=chat` : `/properties/${propertyId}`,
      })
    )
  );
  // Log each failed recipient individually instead of letting one denial
  // fail the whole batch silently — pinpoints exactly which write is denied.
  results.forEach((r, i) => {
    if (r.status === "rejected") {
      const uid = Array.from(recipients.keys())[i];
      console.error(`notifyPropertyParticipants: failed to notify uid=${uid}`, r.reason);
    }
  });
}

// --------------- Listing Inquiries (public listing chat) ---------------
// One conversation per (listing, inquirer), started from the floating chat
// widget on the public "/apply/:listingId" page \u2014 a signed-in prospect can
// message the listing's PM directly, no application required first.
// pmUid/inquirerUid are denormalized onto every message too, so the messages
// subcollection's rules stay plain field checks (no get()/exists() in list
// rules \u2014 see /memories/repo firestore-rules gotcha).

const listingInquiryFromDoc = (d) => ({
  id: d.id,
  ...d.data(),
  createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
  updatedAt: d.data().updatedAt ? d.data().updatedAt.toDate() : null,
  lastMessageAt: d.data().lastMessageAt ? d.data().lastMessageAt.toDate() : null,
});

/** Finds (or starts) the signed-in prospect's conversation for one listing. */
export async function getOrCreateListingInquiry({ listingId, inquirerUid, inquirerName, inquirerEmail }) {
  if (!listingId || !inquirerUid) throw new Error("Missing listing or signed-in user.");
  const existing = await getDocs(
    query(
      collection(db, "listingInquiries"),
      where("listingId", "==", listingId),
      where("inquirerUid", "==", inquirerUid)
    )
  );
  if (!existing.empty) return listingInquiryFromDoc(existing.docs[0]);

  const listing = await getListingById(listingId);
  if (!listing) throw new Error("This listing is no longer available.");
  const pmUid = listing.createdBy || "";
  const inquirerEmailNorm = normalizeEmail(inquirerEmail);
  const ref = await addDoc(collection(db, "listingInquiries"), {
    listingId,
    propertyId: listing.propertyId || "",
    listingAddress: listing.address || "",
    pmUid,
    inquirerUid,
    inquirerName: inquirerName || "",
    inquirerEmail: inquirerEmailNorm,
    lastMessage: "",
    lastMessageAt: null,
    unreadByPm: false,
    unreadByInquirer: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return {
    id: ref.id, listingId, propertyId: listing.propertyId || "", listingAddress: listing.address || "",
    pmUid, inquirerUid, inquirerName: inquirerName || "", inquirerEmail: inquirerEmailNorm,
    lastMessage: "", lastMessageAt: null, unreadByPm: false, unreadByInquirer: false,
  };
}

/** Read-only lookup — unlike getOrCreateListingInquiry, never creates one (used to check for an unread badge before the prospect has opened the widget). */
export async function getListingInquiryForViewer(listingId, inquirerUid) {
  if (!listingId || !inquirerUid) return null;
  const snap = await getDocs(
    query(
      collection(db, "listingInquiries"),
      where("listingId", "==", listingId),
      where("inquirerUid", "==", inquirerUid)
    )
  );
  return snap.empty ? null : listingInquiryFromDoc(snap.docs[0]);
}

/** Live single-document subscription (not a list query, so no composite-index/rule constraints apply). */
export function onListingInquiry(inquiryId, callback) {
  return onSnapshot(doc(db, "listingInquiries", inquiryId), (snap) => {
    callback(snap.exists() ? listingInquiryFromDoc(snap) : null);
  });
}

/** PM: every inquiry across their whole portfolio \u2014 single equality query. */
export async function getListingInquiriesByPm(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, "listingInquiries"), where("pmUid", "==", uid)));
  return snap.docs
    .map(listingInquiryFromDoc)
    .sort((a, b) => (b.lastMessageAt || b.createdAt || 0) - (a.lastMessageAt || a.createdAt || 0));
}

/**
 * PM: every inquiry for one listing, for the grouped-by-inquirer inbox view.
 * Also filters by pmUid — the security rule's list check is an OR on pmUid/
 * inquirerUid, and Firestore can only prove that holds for a list query if
 * one of those fields is itself constrained by an equality filter.
 */
export async function getListingInquiriesByListing(listingId, pmUid) {
  if (!listingId || !pmUid) return [];
  const snap = await getDocs(query(
    collection(db, "listingInquiries"),
    where("listingId", "==", listingId),
    where("pmUid", "==", pmUid)
  ));
  return snap.docs
    .map(listingInquiryFromDoc)
    .sort((a, b) => (b.lastMessageAt || b.createdAt || 0) - (a.lastMessageAt || a.createdAt || 0));
}

/** senderRole is "pm" or "inquirer" — drives the unread flags and who gets notified. */
export async function addListingInquiryMessage(inquiryId, { senderUid, senderRole, senderName, senderEmail, text }) {
  const inquiryRef = doc(db, "listingInquiries", inquiryId);
  const inquirySnap = await getDoc(inquiryRef);
  if (!inquirySnap.exists()) throw new Error("This conversation no longer exists.");
  const inquiry = { id: inquiryId, ...inquirySnap.data() };

  await addDoc(collection(db, "listingInquiries", inquiryId, "messages"), {
    pmUid: inquiry.pmUid,
    inquirerUid: inquiry.inquirerUid,
    senderUid,
    senderRole,
    senderName: senderName || "",
    senderEmail: senderEmail ? normalizeEmail(senderEmail) : "",
    text,
    createdAt: serverTimestamp(),
  });

  const preview = text.length > 80 ? `${text.slice(0, 80)}…` : text;
  await updateDoc(inquiryRef, {
    lastMessage: preview,
    lastMessageAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    unreadByPm: senderRole === "inquirer",
    unreadByInquirer: senderRole === "pm",
  });

  notifyListingInquiryParticipant(inquiry, { senderRole, text: preview })
    .catch((e) => console.error("Notify listing inquiry error:", e));
}

/**
 * Subscribes to one inquiry's chat. Returns an unsubscribe function.
 * viewerRole is "pm" or "inquirer" — the query must filter on that viewer's
 * own uid field (pmUid/inquirerUid) so Firestore can statically prove the
 * messages read rule holds; an unfiltered query can't be proven and is
 * denied outright, even though every message in this subcollection actually
 * carries a matching value. Sorted client-side (not orderBy) so this equality
 * filter doesn't need a composite index — same convention used throughout
 * this file (see getNotificationsByUser/getActivityLog).
 */
export function onListingInquiryMessages(inquiryId, viewerUid, viewerRole, callback) {
  const field = viewerRole === "pm" ? "pmUid" : "inquirerUid";
  const q = query(
    collection(db, "listingInquiries", inquiryId, "messages"),
    where(field, "==", viewerUid)
  );
  return onSnapshot(q, (snap) => {
    const messages = snap.docs
      .map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
      }))
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    callback(messages);
  });
}

/** viewerRole is "pm" or "inquirer" — clears that side's unread flag. */
export async function markListingInquiryRead(inquiryId, viewerRole) {
  const field = viewerRole === "pm" ? "unreadByPm" : "unreadByInquirer";
  await updateDoc(doc(db, "listingInquiries", inquiryId), { [field]: false });
}

async function notifyListingInquiryParticipant(inquiry, { senderRole, text }) {
  if (senderRole === "inquirer") {
    if (!inquiry.pmUid) return;
    await createNotification({
      userId: inquiry.pmUid,
      type: "listing_inquiry",
      title: "New listing inquiry",
      message: `${inquiry.inquirerName || inquiry.inquirerEmail || "Someone"}: ${text}`,
      link: `/listings/${inquiry.listingId}`,
    });
  } else {
    if (!inquiry.inquirerUid) return;
    await createNotification({
      userId: inquiry.inquirerUid,
      type: "listing_inquiry",
      title: "New reply about your inquiry",
      message: text,
      link: `/rentals/${inquiry.listingId}`,
    });
  }
}

/** Realtime unread count across every listing the PM owns, for the sidebar badge. */
export function onUnreadListingInquiriesForPm(uid, callback) {
  const q = query(collection(db, "listingInquiries"), where("pmUid", "==", uid));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.filter((d) => d.data().unreadByPm).length);
  });
}

/** Subscribes to a property's group chat. Returns an unsubscribe function. */
export function onPropertyMessages(propertyId, callback) {
  const q = query(
    collection(db, "properties", propertyId, "messages"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snap) => {
    callback(
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
      }))
    );
  });
}

// --------------- Property Inspections ---------------
// An inspection belongs to a property. The owner (or an editor) creates and
// closes it; every participant — owner, shared users and tenants — can add
// media and take part in the conversation. participantEmails is denormalised
// onto the doc so collection queries stay rule-friendly.

export async function createInspection({
  propertyId, propertyAddress, title, notes, scheduledDate,
  createdBy, createdByEmail, participantEmails,
}) {
  const ref = await addDoc(collection(db, "inspections"), {
    propertyId,
    propertyAddress: propertyAddress || "",
    title: title || "Inspection",
    notes: notes || "",
    scheduledDate: scheduledDate || "",
    status: "open",
    media: [],
    createdBy,
    createdByEmail: normalizeEmail(createdByEmail),
    participantEmails: (participantEmails || []).map(normalizeEmail).filter(Boolean),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Notify every tenant participant (not whoever just created it) so they
  // know there's a new inspection to join — participantEmails alone doesn't
  // tell us who's a tenant vs. a co-owner/editor, so look each one up.
  const creatorEmail = normalizeEmail(createdByEmail);
  const tenantEmails = (participantEmails || [])
    .map(normalizeEmail)
    .filter((e) => e && e !== creatorEmail);
  await Promise.all(
    tenantEmails.map(async (email) => {
      const records = await getTenantsByEmail(email).catch(() => []);
      const match = records.find((t) => t.propertyId === propertyId && t.uid);
      if (!match?.uid) return;
      return createNotification({
        userId: match.uid,
        type: "inspection_created",
        title: "New property inspection",
        message: `A new inspection "${title || "Inspection"}" was created for ${propertyAddress || "your property"}.`,
        link: `/tenant/my-rentals/${match.id}?tab=inspections`,
      }).catch((e) => console.error("Notify inspection created error:", e));
    })
  );

  return ref.id;
}

export async function getInspectionsByProperty(propertyId) {
  if (!propertyId) return [];
  const q = query(collection(db, "inspections"), where("propertyId", "==", propertyId));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getInspectionById(inspectionId) {
  const snap = await getDoc(doc(db, "inspections", inspectionId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) };
}

/** Keeps the participant list in step with who currently has property access. */
export async function updateInspectionParticipants(inspectionId, participantEmails) {
  await updateDoc(doc(db, "inspections", inspectionId), {
    participantEmails: (participantEmails || []).map(normalizeEmail).filter(Boolean),
    updatedAt: serverTimestamp(),
  });
}

export async function addInspectionMedia(inspectionId, media) {
  await updateDoc(doc(db, "inspections", inspectionId), {
    media: arrayUnion(media),
    updatedAt: serverTimestamp(),
  });
}

export async function removeInspectionMedia(inspectionId, media) {
  await updateDoc(doc(db, "inspections", inspectionId), {
    media: arrayRemove(media),
    updatedAt: serverTimestamp(),
  });
}

export async function closeInspection(inspectionId) {
  await updateDoc(doc(db, "inspections", inspectionId), {
    status: "closed",
    closedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function reopenInspection(inspectionId) {
  await updateDoc(doc(db, "inspections", inspectionId), {
    status: "open",
    closedAt: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteInspection(inspectionId) {
  await deleteDoc(doc(db, "inspections", inspectionId));
}

export async function addInspectionMessage(inspectionId, { senderUid, senderName, senderEmail, text }) {
  const ref = await addDoc(collection(db, "inspections", inspectionId, "messages"), {
    senderUid: senderUid || null,
    senderName: senderName || "",
    senderEmail: normalizeEmail(senderEmail),
    text,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/** Subscribes to an inspection's conversation. Returns an unsubscribe function. */
export function onInspectionMessages(inspectionId, callback) {
  const q = query(
    collection(db, "inspections", inspectionId, "messages"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snap) => {
    callback(
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
      }))
    );
  });
}

// --------------- Ticket Messages (Chat) ---------------

/**
 * Adds a chat message to a ticket's messages subcollection.
 */
export async function addTicketMessage(portalId, ticketId, { senderUid, senderName, senderEmail, text }) {
  const messagesRef = collection(db, "portals", portalId, "tickets", ticketId, "messages");
  const docRef = await addDoc(messagesRef, {
    senderUid,
    senderName,
    senderEmail: senderEmail || "",
    text,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Subscribes to real-time chat messages for a ticket.
 * Returns an unsubscribe function.
 */
export function onTicketMessages(portalId, ticketId, callback) {
  const q = query(
    collection(db, "portals", portalId, "tickets", ticketId, "messages"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(q, (snap) => {
    const messages = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt ? d.data().createdAt.toDate() : null,
    }));
    callback(messages);
  });
}

// --------------- Lease CRUD ---------------

export async function createLease(data) {
  const ref = await addDoc(collection(db, "leases"), {
    ...data,
    status: "Active",
    signedByTenants: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  logActivity({
    uid: data.createdBy,
    type: "lease_created",
    title: "Payment setup created",
    message: `Set up rent collection for ${data.propertyAddress || "a property"} ($${data.monthlyRent || 0}/mo)`,
    link: data.propertyId ? `/properties/${data.propertyId}` : null,
  }).catch((e) => console.error("Log activity error:", e));

  return ref.id;
}

export async function getLeaseById(leaseId) {
  const snap = await getDoc(doc(db, "leases", leaseId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...convertTimestamps(snap.data()) };
}

export async function getLeasesByOwner(uid) {
  const q = query(
    collection(db, "leases"),
    where("createdBy", "==", uid)
  );
  const snap = await getDocs(q);
  const results = snap.docs.map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }));
  return results
    .filter((r) => !r.deleted)
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getDeletedLeasesByOwner(uid) {
  const q = query(collection(db, "leases"), where("createdBy", "==", uid));
  const snap = await getDocs(q);
  const results = snap.docs.map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }));
  return results
    .filter((r) => r.deleted === true)
    .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));
}

export async function getLeasesByProperty(propertyId) {
  const q = query(
    collection(db, "leases"),
    where("propertyId", "==", propertyId)
  );
  const snap = await getDocs(q);
  const results = snap.docs.map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }));
  return results.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getActiveLeaseByProperty(propertyId) {
  const q = query(
    collection(db, "leases"),
    where("propertyId", "==", propertyId)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  // Filter for active, non-deleted leases client-side to avoid composite index requirement
  const active = snap.docs.find((d) => {
    const data = d.data();
    return data.status === "Active" && !data.deleted;
  });
  if (!active) return null;
  return { id: active.id, ...convertTimestamps(active.data()) };
}

export async function getLeaseByTenantEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;
  const q = query(
    collection(db, "leases"),
    where("tenantEmails", "array-contains", normalized)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  // Filter for active leases client-side to avoid composite index requirement
  const active = snap.docs.find((d) => d.data().status === "Active");
  if (!active) return null;
  return { id: active.id, ...convertTimestamps(active.data()) };
}

/**
 * Returns ALL leases where this email appears in tenantEmails.
 * Unlike getLeaseByTenantEmail (which returns only the first active one),
 * this returns every lease regardless of status — so historical leases
 * remain visible even after the tenant is removed from a property.
 */
export async function getAllLeasesByTenantEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return [];
  const q = query(
    collection(db, "leases"),
    where("tenantEmails", "array-contains", normalized)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function updateLease(leaseId, data) {
  await updateDoc(doc(db, "leases", leaseId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function softDeleteLease(leaseId) {
  await updateDoc(doc(db, "leases", leaseId), {
    deleted: true,
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function restoreLease(leaseId) {
  await updateDoc(doc(db, "leases", leaseId), {
    deleted: false,
    deletedAt: null,
    updatedAt: serverTimestamp(),
  });
}

export async function signLease(leaseId, tenantId, tenantEmail) {
  await updateDoc(doc(db, "leases", leaseId), {
    signedByTenants: arrayUnion({ tenantId, email: tenantEmail, signedAt: new Date().toISOString() }),
    updatedAt: serverTimestamp(),
  });
}

export async function getLeasesByPortal(portalId) {
  const q = query(
    collection(db, "leases"),
    where("portalId", "==", portalId)
  );
  const snap = await getDocs(q);
  const results = snap.docs.map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }));
  return results.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

// ── Payments ─────────────────────────────────────────────────────────────────
// Payment records are written server-side by the stripeWebhook Lambda (Admin
// SDK, bypasses these rules) once Stripe confirms a charge — there is no
// client-side create function anymore; see /memories/repo/payments-stripe.md.

// Tenant: queries by own uid (createdBy) so the rule condition aligns with the where clause.
// Only returns payments this tenant personally triggered — for the whole
// household's history (including co-tenants' payments) use paymentsApi.getLeasePayments,
// a server-side Lambda (the client-side leaseTenantEmails-array-contains equivalent
// kept failing live with "Missing or insufficient permissions").
export async function getMyPayments(createdByUid, leaseId = null) {
  const q = query(collection(db, "payments"), where("createdBy", "==", createdByUid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .filter((p) => !leaseId || p.leaseId === leaseId)
    .sort((a, b) => (b.paidAt || b.createdAt || 0) - (a.paidAt || a.createdAt || 0));
}

// PM: queries by pmUid so the rule condition aligns with the where clause.
export async function getPaymentsByLease(pmUid, leaseId) {
  const q = query(collection(db, "payments"), where("pmUid", "==", pmUid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .filter((p) => p.leaseId === leaseId)
    .sort((a, b) => (b.paidAt || b.createdAt || 0) - (a.paidAt || a.createdAt || 0));
}

/** PM: every payment collected across all of their properties. */
export async function getPaymentsByPm(pmUid) {
  if (!pmUid) return [];
  const q = query(collection(db, "payments"), where("pmUid", "==", pmUid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .sort((a, b) => (b.paidAt || b.createdAt || 0) - (a.paidAt || a.createdAt || 0));
}

/**
 * Logs an off-platform (cash/check) rent or security-deposit payment the PM
 * collected outside Stripe — the only client-side write the `payments` rule
 * allows, tagged `source: "manual"` so it's never confused with a real
 * Stripe charge. `paidAt` may be any past date (e.g. to backfill/test
 * late-rent detection). `month` is required for `type: "rent"` and ignored
 * (stored as null) for `type: "deposit"`, since a deposit isn't tied to a month.
 */
export async function recordManualPayment(pmUid, {
  leaseId, propertyId, tenantUid, tenantEmail, tenantName, type = "rent", month, amount, paidAt, note,
}) {
  // Denormalized so every co-tenant on this lease (not just the one this
  // payment is tagged to) can see it — see the payments rule's leaseTenantEmails check.
  const lease = await getLeaseById(leaseId).catch(() => null);
  const leaseTenantEmails = (lease?.tenantEmails || []).map(normalizeEmail).filter(Boolean);
  const ref = await addDoc(collection(db, "payments"), {
    leaseId,
    propertyId: propertyId || null,
    pmUid,
    createdBy: tenantUid || null,
    tenantEmail: tenantEmail || "",
    tenantName: tenantName || "",
    leaseTenantEmails,
    type,
    month: type === "deposit" ? null : month,
    amount: Number(amount) || 0,
    source: "manual",
    note: note || "",
    paidAt: paidAt ? new Date(`${paidAt}T00:00:00`) : serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  if (tenantUid) {
    const what = type === "deposit" ? "security deposit payment" : `payment for ${month}`;
    createNotification({
      userId: tenantUid,
      type: "payment_received",
      title: "Payment recorded",
      message: `Your landlord recorded a $${Number(amount) || 0} ${what}.`,
      link: "/tenant/my-rentals",
    }).catch((e) => console.error("Notify manual payment error:", e));
  }

  return ref.id;
}

/**
 * Edits a manual (off-platform) payment record — the Firestore rule only
 * allows this when the target doc's `source` is already "manual" and the
 * caller is its `pmUid`, so a real Stripe-verified charge can never be
 * altered this way. `month` is ignored (stored as null) for `type: "deposit"`.
 */
export async function updateManualPayment(paymentId, {
  tenantUid, tenantEmail, tenantName, type = "rent", month, amount, paidAt, note,
}) {
  await updateDoc(doc(db, "payments", paymentId), {
    createdBy: tenantUid || null,
    tenantEmail: tenantEmail || "",
    tenantName: tenantName || "",
    type,
    month: type === "deposit" ? null : month,
    amount: Number(amount) || 0,
    note: note || "",
    paidAt: paidAt ? new Date(`${paidAt}T00:00:00`) : serverTimestamp(),
  });
}

/** Removes a manual (off-platform) payment record — same "manual only"
 * restriction as updateManualPayment, enforced by the Firestore rule. */
export async function deleteManualPayment(paymentId) {
  await deleteDoc(doc(db, "payments", paymentId));
}

/** Notifies the PM that a tenant's rent/deposit payment succeeded (fired
 * client-side right after Stripe confirms — the authoritative `payments`
 * doc is still written separately by the stripeWebhook Lambda).
 * `description` is a human summary of what was paid, e.g. "rent for 2026-01"
 * or "security deposit and rent for 2026-01, 2026-02". */
export async function notifyPaymentReceived({ pmUid, propertyId, tenantName, amount, description }) {
  if (!pmUid) return;
  await createNotification({
    userId: pmUid,
    type: "payment_received",
    title: "Rent payment received",
    message: `${tenantName || "A tenant"} paid $${amount || 0} for ${description || "rent"}.`,
    link: propertyId ? `/properties/${propertyId}` : null,
  });
}

const LATE_RENT_GRACE_DAYS = 3;

/**
 * Checks whether the current month's rent is overdue for an active lease
 * and, if so, notifies the PM once per lease per month (dedup via a
 * `lastLateNotifiedMonth` field stamped onto the lease doc). Only the PM can
 * freely update a lease, so this must be called from a PM-side page.
 */
export async function checkAndNotifyLateRent(lease, payments) {
  if (!lease || lease.status !== "Active" || lease.deleted) return;

  const dueDay = parseInt(lease.rentDueDay) || 1;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear();
  const m = today.getMonth();
  const dueDate = new Date(y, m, dueDay);
  const lateThreshold = new Date(dueDate);
  lateThreshold.setDate(lateThreshold.getDate() + LATE_RENT_GRACE_DAYS);
  if (today <= lateThreshold) return;

  const monthStr = `${y}-${String(m + 1).padStart(2, "0")}`;
  const alreadyPaid = (payments || []).some(
    (p) => p.month === monthStr || (Array.isArray(p.items) && p.items.some((it) => it.type === "rent" && it.month === monthStr))
  );
  if (alreadyPaid) return;
  if (lease.lastLateNotifiedMonth === monthStr) return;

  await createNotification({
    userId: lease.createdBy,
    type: "rent_late",
    title: "Rent is late",
    message: `${(lease.tenantNames || []).join(", ") || "A tenant"}'s rent for ${lease.propertyAddress || "a property"} is overdue.`,
    link: lease.propertyId ? `/properties/${lease.propertyId}` : null,
  });
  await updateLease(lease.id, { lastLateNotifiedMonth: monthStr });
}

/**
 * Record the landlord's signature on a lease.
 * Stores name, email, uid and timestamp.
 */
export async function signLeaseAsLandlord(leaseId, { uid, name, email }) {
  await updateDoc(doc(db, "leases", leaseId), {
    landlordSignature: {
      uid,
      name,
email: normalizeEmail(email),
      signedAt: new Date().toISOString(),
    },
    updatedAt: serverTimestamp(),
  });
}

// --------------- Role-based queries (uid-indexed) ---------------

/**
 * Returns all active tenant records linked to a Firebase Auth uid.
 * Populated when a tenant accepts an invitation.
 */
export async function getTenantsByUid(uid) {
  if (!uid) return [];
  const q = query(collection(db, "tenants"), where("uid", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...convertTimestamps(d.data()) }))
    .filter((d) => !d.deleted);
}

// --------------- Invitations ---------------

export async function createInvitation({ email, role, targetId, leaseId, propertyId, createdByUid }) {
  const ref = await addDoc(collection(db, "invitations"), {
    email: normalizeEmail(email),
    role,           // "tenant"
    targetId,       // Firestore doc id in the tenants collection
    leaseId: leaseId || null,
    propertyId: propertyId || null,
    createdByUid,
    status: "pending",
    createdAt: serverTimestamp(),
    respondedAt: null,
  });
  logActivity({
    uid: createdByUid,
    type: "tenant_invited",
    title: "Tenant invited",
    message: `Invited ${normalizeEmail(email)} as a ${role}`,
    link: propertyId ? `/properties/${propertyId}` : null,
  }).catch((e) => console.error("Log activity error:", e));
  return ref.id;
}

export async function getInvitationsByCreator(uid) {
  const q = query(
    collection(db, "invitations"),
    where("createdByUid", "==", uid)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate() || null }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function getInvitationsByEmail(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return [];
  const q = query(
    collection(db, "invitations"),
    where("email", "==", normalized),
    where("status", "==", "pending")
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate() || null }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

/**
 * Real-time subscription to pending invitations addressed to a user's email.
 * Returns an unsubscribe function.
 */
export function onPendingInvitations(email, callback) {
  const normalized = normalizeEmail(email);
  if (!normalized) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, "invitations"),
    where("email", "==", normalized),
    where("status", "==", "pending")
  );
  return onSnapshot(q, (snap) => {
    const invites = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate() || null,
    }));
    callback(invites);
  });
}

/**
 * Accept an invitation: links uid to the tenant record and marks it accepted.
 */
export async function acceptInvitation(invitationId, uid, displayName = "") {
  const invRef = doc(db, "invitations", invitationId);
  const invSnap = await getDoc(invRef);
  if (!invSnap.exists()) throw new Error("Invitation not found");
  const { targetId } = invSnap.data();

  const batch = writeBatch(db);
  batch.update(doc(db, "tenants", targetId), {
    uid,
    ...(displayName ? { name: displayName } : {}),
    inviteStatus: "accepted",
    acceptedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  batch.update(invRef, {
    status: "accepted",
    respondedAt: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * Decline an invitation: marks the tenant record declined and unlinks it so the
 * property owner sees it disappear and can re-invite if it was a mistake.
 */
export async function declineInvitation(invitationId) {
  const invRef = doc(db, "invitations", invitationId);
  const invSnap = await getDoc(invRef);
  if (!invSnap.exists()) throw new Error("Invitation not found");
  const { targetId } = invSnap.data();

  const batch = writeBatch(db);
  if (targetId) {
    batch.update(doc(db, "tenants", targetId), {
      uid: null,
      inviteStatus: "declined",
      declinedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  batch.update(invRef, {
    status: "declined",
    respondedAt: serverTimestamp(),
  });
  await batch.commit();
}

// --------------- Top-level Tickets (RETIRED) ---------------
// This original top-level `tickets` collection was replaced by the
// self-contained `tickets` collection near createPropertyTicket() above —
// see /memories/repo/notifications-audit-tickets.md for the history
// (briefly went through a portals/{portalId}/tickets design in between).
